import { Alert, Badge, Center, Group, Loader, Stack, Text, Title, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconFlask } from '@tabler/icons-react';
import { type QueryKey, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { api, unwrap } from './api.ts';
import { problemMessage } from './format.ts';

export function Page(props: {
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <div>
          <Title order={2}>{props.title}</Title>
          {props.description ? (
            <Text c="dimmed" size="sm" mt={4}>
              {props.description}
            </Text>
          ) : null}
        </div>
        {props.actions ? <Group gap="sm">{props.actions}</Group> : null}
      </Group>
      {props.children}
    </Stack>
  );
}

export function Failure(props: { error: unknown }) {
  if (!props.error) return null;
  return (
    <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} role="alert">
      {problemMessage(props.error)}
    </Alert>
  );
}

export function Loading() {
  return (
    <Center py="xl">
      <Loader color="ink" size="sm" />
    </Center>
  );
}

export function Empty(props: { children: ReactNode }) {
  return (
    <Text c="dimmed" ta="center" py="xl" size="sm">
      {props.children}
    </Text>
  );
}

/** Marks screens and rows that show typed demo data because the API has no endpoint for them yet. */
export function DemoBadge(props: { what?: string }) {
  return (
    <Tooltip
      label={`${props.what ?? 'This'} has no API yet; the rows are typed demo data from @budovan/domain`}
    >
      <Badge variant="light" color="yellow" leftSection={<IconFlask size={12} />}>
        Demo data
      </Badge>
    </Tooltip>
  );
}

/** The signed-in person, fetched once and shared by the shell and every screen. */
export const useMe = () =>
  useQuery({ queryKey: ['me'], queryFn: () => unwrap(api().GET('/v1/me')), staleTime: 60_000 });

/**
 * A write with the feedback every screen wants: a toast on success or with the problem on failure, and
 * the affected queries refetched. Problems are not swallowed: the mutation keeps its error for forms.
 */
export function useSave<TInput, TResult>(options: {
  run: (input: TInput) => Promise<TResult>;
  invalidates: QueryKey[];
  success?: string | ((result: TResult) => string);
  onDone?: (result: TResult) => void;
}) {
  const queries = useQueryClient();
  return useMutation({
    mutationFn: options.run,
    onSuccess: async (result) => {
      await Promise.all(options.invalidates.map((queryKey) => queries.invalidateQueries({ queryKey })));
      const message = typeof options.success === 'function' ? options.success(result) : options.success;
      if (message) notifications.show({ color: 'green', message });
      options.onDone?.(result);
    },
    onError: (error) =>
      notifications.show({ color: 'red', title: 'Not saved', message: problemMessage(error) }),
  });
}
